import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { useT } from "../i18n/LocaleProvider";

// "Auto Play" is a global preference (set from the home page) rather than a
// per-drawing button: when ON, opening any drawing starts the hands-free video
// loop automatically (DrawingPlayer reads `enabled` on mount). Off by default —
// the normal experience is drawing along step by step.
const AUTOPLAY_KEY = "learn-draw:autoPlay";

interface AutoPlayValue {
  enabled: boolean;
  toggle: () => void;
}

const AutoPlayContext = createContext<AutoPlayValue | null>(null);

function loadEnabled(): boolean {
  try {
    return localStorage.getItem(AUTOPLAY_KEY) === "1"; // off by default
  } catch {
    return false;
  }
}

export function AutoPlayProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabled] = useState(loadEnabled);
  const toggle = useCallback(() => {
    setEnabled((on) => {
      const next = !on;
      try {
        localStorage.setItem(AUTOPLAY_KEY, next ? "1" : "0");
      } catch {
        /* ignore storage errors (e.g. private mode) */
      }
      return next;
    });
  }, []);
  return (
    <AutoPlayContext.Provider value={{ enabled, toggle }}>
      {children}
    </AutoPlayContext.Provider>
  );
}

export function useAutoPlay(): AutoPlayValue {
  const ctx = useContext(AutoPlayContext);
  if (!ctx) throw new Error("useAutoPlay must be used within an AutoPlayProvider");
  return ctx;
}

/** The 🎬 Auto-Play on/off toggle (lives on the home page). */
export function AutoPlayButton() {
  const { enabled, toggle } = useAutoPlay();
  const t = useT();
  return (
    <button
      className={enabled ? "sound-btn on" : "sound-btn"}
      onClick={toggle}
      aria-label={enabled ? t.autoPlayOnAria : t.autoPlayOffAria}
      aria-pressed={enabled}
    >
      🎬
      <span className="sound-btn-label">{enabled ? t.autoPlayOn : t.autoPlayOff}</span>
    </button>
  );
}
